import { message, notification } from "antd";
import gifshot from "gifshot";
import { format } from "date-fns";
import GA4React from "ga-4-react";
import {
  numericDateFnsFormat,
  normalizeTeamRegion,
} from "../utils/teamRegion";
import { SelectedMomentosData } from "../interfaces/moment";
import { browserName } from "../utils/browser";
import { MomentServiceInstance } from "./moment";
import { isFanMomentScoreboardEnabled } from "../utils/momentScoreboardFan";
import { TicketTemplate } from "../shared/interfaces";
import { StorageServiceInstance } from "./storage";
import { GIF_FRAME_DELAY_MS } from "../utils/gif";
import {
  MomentResponse,
  CroppedVideoFormat,
} from "../shared/interfaces/moment";
import { IntervalGamePopulated, TEMPLATE_TYPES } from "../shared/interfaces/game";
import { Routes } from "../utils/routes";
import { ImageWithSizes, prepareAndSaveTicketImage, prepareTemplateImage } from "../utils/sharedTicketFunctions";
import {
  MomentHeaderData, releaseCanvas, renderSponsorOverlay,
  renderMomentHeader, DownloadData, convertToBase64, urltoFile,
  uploadToS3ServerLargeImage,
  originalConvertToBase64,
  loadImage,
} from "../utils/sharedFunctions";
import { uploadImage, getStadiumName } from "../axios/routes/image";
import { resolveDownloadSeatTokens } from "../utils/downloadSeatTokens";
import { getTemplateDesignName } from "../utils/ticketTemplate";
import {
  trackFanFileDownload,
  trackFanSocialShare,
} from "../utils/clientAnalytics";

interface GifshotResponse {
  error?: string;
  image: string;
}

class FileLoaderService {
  private ga4React = GA4React.getGA4React();

  private waitingForVideo = false;

  private logShareEvent = () => {
    this.logEvent("moment_share");
  };

  /** Fire-and-forget upload to S3 for operator downloads page. Does not block or throw. */
  private tryUploadDownloadToS3 = (
    imageUrl: string,
    fileType: "image" | "ticket" | "gif",
    ticketTemplateName?: string,
  ) => {
    const {
      selectedGameData,
      selectedSection,
      selectedRow,
      selectedSeat,
      selectedCustomerId,
      selectedEmail,
      selectedSpecialSection,
      selectedSectionIsSpecial,
      selectedPlaceX,
      selectedPlaceY,
    } = StorageServiceInstance;
    if (!selectedGameData?.stadium) return;
    const stadium = getStadiumName(selectedGameData.stadium);
    const team1Name = (selectedGameData.team1 as { name?: string })?.name || "Team1";
    const team2Name = (selectedGameData.team2 as { name?: string })?.name || "Team2";
    const { section, row, seat } = resolveDownloadSeatTokens({
      selectedSection,
      selectedRow,
      selectedSeat,
      selectedSpecialSection,
      selectedSectionIsSpecial,
      selectedPlaceX,
      selectedPlaceY,
      selectedCustomerId,
      selectedEmail,
      isGa: StorageServiceInstance.isGaMomentContext(),
    });
    uploadImage({
      imageUrl,
      action: "download",
      stadium,
      team1Name,
      team2Name,
      gameStartDateMs: selectedGameData.startDate ?? null,
      gameId: selectedGameData._id,
      customerId: selectedCustomerId,
      email: selectedEmail,
      section,
      row,
      seat,
      fileType,
      ticketTemplateName,
    }).catch((err) => console.warn("Upload to S3 for operator downloads failed:", err));
  };

  private logEvent = (eventName: string) => {
    if (typeof this.ga4React !== "object") return;
    const game = StorageServiceInstance.selectedGameData;
    if (!game?._id || !game.team1 || !game.team2) return;
    this.ga4React.gtag("event", eventName, {
      event_label: null,
      event_category: "engagement",
      non_interaction: false,
      game_id: game._id,
      game_name: `${game.team1.name} vs. ${game.team2.name} @ ${
        game.stadium
      }, ${game.city}, ${format(
        game.startDate,
        numericDateFnsFormat(normalizeTeamRegion(game.team1?.teamRegion)),
      )}`,
    });
  };

  public trackEvent(eventName: string) {
    this.logEvent(eventName);
  }

  public isWebView = () => {
    if (typeof window === undefined) {
      return false;
    }

    let isWebView = false;
    const userAgent = navigator?.userAgent || "";

    if (/Android/.test(userAgent)) {
      const androidVersion = parseFloat(userAgent.slice(userAgent.indexOf("Android") + 8));
      if (androidVersion >= 10) {
        isWebView = /(wv)/.test(userAgent);
      } else {
        isWebView = userAgent.includes("Version/");
      }
    } else {
      // @ts-ignore
      const standalone = navigator?.standalone || false;
      const userAgentLowerCase = userAgent.toLowerCase();
      const safari = /safari/.test(userAgentLowerCase);
      const ios = /iphone|ipod|ipad/.test(userAgentLowerCase);

      isWebView = ios ? !standalone && !safari : userAgent.includes("wv");
    }

    return isWebView;
  }

  public isDesktopChrome = () => {
    const userAgent = navigator?.userAgent || "";

    const isMobile = (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(userAgent));
    const isChrome = (/Chrome/i.test(userAgent));
    const isDesktopChrome = isChrome && !isMobile;

    return isDesktopChrome;
  }

  private safariDownloadInterval = 1000;

  public shareMomento = async (buttonColor?: string) => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArray();
    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Image = await convertToBase64(
      downloadDataArray[0],
      sponsorImage,
      headerData,
      StorageServiceInstance.blurImage,
    );

    const file = await urltoFile(base64Image, "momento.png", "image/png");

    this.shareViaNavigator([file], "Moment is ready to be shared!", "Image", buttonColor);
  };

  public shareWholeMoment = async (buttonColor?: string) => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArray(true);
    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Promises = downloadDataArray.map((d) => convertToBase64(d, sponsorImage, headerData));

    const base64Images = await Promise.all(base64Promises);

    const filesPromises = base64Images.map((i) => urltoFile(i, "moment.png", "image/png"));

    const files = await Promise.all(filesPromises);

    this.shareViaNavigator(files, "Moments are ready to be shared!", "Image", buttonColor);
  };

  public shareSelectedMomentos = async (
    selectedIndexes: SelectedMomentosData,
    buttonColor?: string,
  ) => {
    const downloadDataArray = MomentServiceInstance.getDownloadSelectedMomentosArray(selectedIndexes);
    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Promises = downloadDataArray.map((d) => convertToBase64(d, sponsorImage, headerData));

    const base64Images = await Promise.all(base64Promises);

    const filesPromises = base64Images.map((i) => urltoFile(i, "moment.png", "image/png"));

    const files = await Promise.all(filesPromises);

    this.shareViaNavigator(files, "Moment is ready to be shared!", "Image", buttonColor);
  };

  private shareGeneratedGif = async (gif: string, buttonColor?: string) => {
    const file = await urltoFile(
      gif,
      "moment.gif",
      "image/gif",
    );

    this.shareViaNavigator(
      [file],
      "Moment is ready to be shared!",
      "Video",
      buttonColor,
    );
  };

  public shareGifMoment = async (buttonColor?: string) => {
    const targetMomentIndex = MomentServiceInstance.momentList.findIndex(
      (m) => m._id === MomentServiceInstance.selectedMoment._id,
    );

    if (
      targetMomentIndex >= 0
      && targetMomentIndex < MomentServiceInstance.momentsData.length
    ) {
      if (MomentServiceInstance.cacheService.loading) {
        message.info("Please wait for a few seconds to prepare all images and repeat");
        return;
      }

      message.info("GIF is being created. Please wait for a few seconds");
      const momentData = MomentServiceInstance.momentsData[targetMomentIndex];

      const gifImage = await MomentServiceInstance.cacheService.renderSingleGifForSharing(
        MomentServiceInstance.selectedMoment._id,
      );

      if (gifImage) {
        await this.shareGeneratedGif(gifImage, buttonColor);
      } else {
        message.error("Error during GIF generation");
      }

      return;
    }

    message.error("No GIF Image");
  };

  public shareGifMomentWithGeneration = async (buttonColor?: string) => {
    message.info("GIF is being created. Please wait for a few seconds");

    const downloadDataArray = MomentServiceInstance.getDownloadDataArray(
      true,
      true,
    );
    const sponsorImage = await this.loadSponsorImage();
    // GIF encode omits the moment scoreboard header even when scoreboard-on-photo is on for stills.
    const base64Promises = downloadDataArray.map((d) => convertToBase64(d, sponsorImage, undefined));

    const base64Images = await Promise.all(base64Promises);

    gifshot.createGIF(
      {
        gifWidth: downloadDataArray[0].width,
        gifHeight: downloadDataArray[0].height,
        images: base64Images,
        interval: GIF_FRAME_DELAY_MS / 1000,
      },
      async (response: GifshotResponse) => {
        if (response.error) {
          message.error(
            "Something went wrong while creating a GIF for download",
          );
          return;
        }

        const file = await urltoFile(
          response.image,
          "moment.gif",
          "image/gif",
        );

        this.shareViaNavigator(
          [file],
          "Moment is ready to be shared!",
          "Video",
          buttonColor,
        );
      },
    );
  };

  public shareMP4Moment = async (buttonColor?: string) => {
    if (navigator.share === undefined) {
      const gifBlobHolder = {
        outputFormat: CroppedVideoFormat.GIF,
        blobResult: null,
      };
      const localGifBlobUrl: string = await this.prepareVideoForDownload(
        true,
        gifBlobHolder,
      );
    } else {
      const mp4BlobHolder = { blobResult: null };
      const localMP4BlobUrl: string = await this.prepareVideoForDownload(
        false,
        mp4BlobHolder,
      );
      const file = new File([mp4BlobHolder.blobResult], "moment.mp4", {
        type: mp4BlobHolder.blobResult.type,
      });

      this.shareViaNavigator([file], "Momento Video", "Video", buttonColor);
    }
  };

  public prepareHeaderData = (force = false) : MomentHeaderData | undefined => {
    if (force || StorageServiceInstance.selectedShowMomentCaption) {
      const gameData = StorageServiceInstance.selectedGameData;
      return {
        moment: MomentServiceInstance.selectedMoment,
        selectedGameData: gameData,
        team1LogoImage: StorageServiceInstance.team1logoImage,
        team2LogoImage: StorageServiceInstance.team2logoImage,
        logoImage: StorageServiceInstance.mainLogoImage,
        team1Color: StorageServiceInstance.team1Color,
        team2Color: StorageServiceInstance.team2Color,
        team1SecondaryColor: gameData?.team1?.secondaryColor,
        team2SecondaryColor: gameData?.team2?.secondaryColor,
        hideScoreboard: !isFanMomentScoreboardEnabled(),
        scoreboardBackgroundColor: gameData?.team1?.momentScoreboardBackgroundColor?.trim() || undefined,
        scoreboardDescriptionColor: gameData?.team1?.momentScoreboardDescriptionColor?.trim() || undefined,

        minWidth: 150,
        maxWidth: 300,
        team1SponsorLogoImage: StorageServiceInstance.team1SponsorLogoImage,
      };
    }

    return undefined;
  };

  /** Ensure team logos are loaded before header - loads from URL if not in storage */
  public prepareHeaderDataWithLogos = async (force = false): Promise<MomentHeaderData | undefined> => {
    const base = this.prepareHeaderData(force);
    if (!base?.selectedGameData) return base;

    let team1Img = base.team1LogoImage;
    let team2Img = base.team2LogoImage;
    const team1Url = base.selectedGameData?.team1?.logo;
    const team2Url = base.selectedGameData?.team2?.logo;

    if (!team1Img?.width && team1Url) {
      try {
        const { image } = await loadImage(team1Url, false, 4);
        if (image?.width) team1Img = image;
      } catch {
        // keep null
      }
    }
    if (!team2Img?.width && team2Url) {
      try {
        const { image } = await loadImage(team2Url, false, 4);
        if (image?.width) team2Img = image;
      } catch {
        // keep null
      }
    }

    return { ...base, team1LogoImage: team1Img, team2LogoImage: team2Img };
  };

  public prepareSingleMomentoImage = async (showHeader: boolean) => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArray();
    const sponsorImage = await this.loadSponsorImage();
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : null;
    const raw = downloadDataArray[0];
    if (!raw?.link) {
      return null;
    }
    // Use saved crop data so downloaded photo reflects the user's crop
    const base64Image = await convertToBase64(
      raw,
      sponsorImage,
      headerData,
      StorageServiceInstance.blurImage,
    );

    return base64Image;
  };

  public downloadSingleMomento = async (wnd?: Window) => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArray();
    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Image = await convertToBase64(
      downloadDataArray[0],
      sponsorImage,
      headerData,
      StorageServiceInstance.blurImage,
    );

    this.tryUploadDownloadToS3(base64Image, "image");
    this.createUrlAndDownloadImage(base64Image, `${downloadDataArray[0].name}.png`, wnd);
    StorageServiceInstance.requestTeamPopupForOccurrence("momento_image_download");
  };

  public downloadTicket = async (ticket: string, wnd?: Window) => {
    const templateName = getTemplateDesignName(
      TEMPLATE_TYPES.MAIN,
      StorageServiceInstance.selectedGameData || undefined,
    );
    this.tryUploadDownloadToS3(ticket, "ticket", templateName);
    this.createUrlAndDownloadImage(ticket, "ticket.png", wnd, false, 2);
    StorageServiceInstance.requestTeamPopupForOccurrence("momento_ticket_download");
  };

  public shareTicket = async (ticket: string, buttonColor?: string) => {
    const file = await urltoFile(ticket, "ticket.png", "image/png");

    this.shareViaNavigator([file], "Ticket is ready to be shared!", "Image", buttonColor);
  };

  /** Direct native share - opens iOS/Android share sheet immediately, no iframe/modal. */
  public shareTicketDirectToNative = async (
    base64Image: string,
    filename = "momento.png",
    mimeType = "image/png",
    shareText?: string,
    shareUrl?: string,
  ) => {
    const file = await urltoFile(base64Image, filename, mimeType);
    this.logShareEvent();
    const url = shareUrl?.trim();
    let text = shareText || "Check out my authentic gameday reaction! @seeyourmomento #MyMomento";
    if (url && text.includes(url)) {
      text = text.split(url).join("").replace(/\n{3,}/g, "\n\n").trim();
    }
    const shareData: ShareData = {
      text,
      files: [file],
      title: "Momento",
    };
    if (url) {
      shareData.url = url;
    }
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      message.error("Your device does not support sharing.");
    }
  };

  public savePurchasedImage = async (
    selectedMomento?: number,
    addSponsorImage?: boolean,
    s3Folder?: string,
    filename?: string,
  ): Promise<ImageWithSizes | null> => {
    const momentoIndex = MomentServiceInstance.getMomentoIndex(selectedMomento);
    const downloadDataArray = MomentServiceInstance.getDownloadDataArrayForMomento(momentoIndex);
    const sponsorImage = addSponsorImage ? await this.loadSponsorImage() : null;
    const base64Image = await convertToBase64(
      downloadDataArray[0],
      sponsorImage,
      null,
      StorageServiceInstance.blurImage,
    );

    const file = await urltoFile(base64Image, "momento.png", "image/png");
    const fileName = filename || `${Date.now()}.png`;

    const result = await uploadToS3ServerLargeImage(
      file,
      fileName,
      "image/png",
      s3Folder,
    );

    return {
      dataUrl: result,
      width: downloadDataArray[0].width,
      height: downloadDataArray[0].height,
    };
  }

  public saveTicketImage = async (
    selectedMomento: number,
    templateType: TEMPLATE_TYPES = TEMPLATE_TYPES.MAIN,
    onlyPrepare = false,
    s3Folder?: string,
    filename?: string,
    showWatermark = false,
    watermarkLogoOpacity = 0.4,
  ): Promise<ImageWithSizes | null> => {
    await StorageServiceInstance.ensureGameTemplatesLoaded();
    const downloadDataArray = MomentServiceInstance.getDownloadDataArrayForMomento(selectedMomento);
    const { selectedGameData } = StorageServiceInstance;
    const { selectedMoment } = MomentServiceInstance;
    if (downloadDataArray.length <= 0 || !selectedGameData?.ticketTemplateImageKey) {
      return null;
    }
    const imageData = downloadDataArray[0];
    const headerData = await this.prepareHeaderDataWithLogos(true);

    let { ticketTemplateImageKey, template } = selectedGameData;

    switch (templateType) {
      case TEMPLATE_TYPES.MAIN:
        if (selectedMoment?.ticketTemplate && selectedMoment?.ticketTemplateImageKey) {
          const { template: momentTemplate, ticketTemplateImageKey: imageKey } = selectedMoment;
          template = momentTemplate;
          ticketTemplateImageKey = imageKey;
        }
        break;
      case TEMPLATE_TYPES.GAME_MAIN:
        break;
      case TEMPLATE_TYPES.FAN1st:
        ticketTemplateImageKey = selectedGameData.fan1stTicketTemplateImageKey;
        template = selectedGameData.fan1stTemplate;
        break;
      case TEMPLATE_TYPES.SPECIAL_SECTION:
        ticketTemplateImageKey = selectedGameData.specialSectionTicketTemplateImageKey;
        template = selectedGameData.specialSectionTemplate;
        break;
      case TEMPLATE_TYPES.SPECIAL_1:
        ticketTemplateImageKey = selectedGameData.specialTicketTemplateImageKey1;
        template = selectedGameData.specialTemplate1;
        break;
      case TEMPLATE_TYPES.SPECIAL_2:
        ticketTemplateImageKey = selectedGameData.specialTicketTemplateImageKey2;
        template = selectedGameData.specialTemplate2;
        break;
      case TEMPLATE_TYPES.SPECIAL_3:
        ticketTemplateImageKey = selectedGameData.specialTicketTemplateImageKey3;
        template = selectedGameData.specialTemplate3;
        break;
      case TEMPLATE_TYPES.SPECIAL_4:
        ticketTemplateImageKey = selectedGameData.specialTicketTemplateImageKey4;
        template = selectedGameData.specialTemplate4;
        break;
      case TEMPLATE_TYPES.SPECIAL_5:
        ticketTemplateImageKey = selectedGameData.specialTicketTemplateImageKey5;
        template = selectedGameData.specialTemplate5;
        break;
      case TEMPLATE_TYPES.SPECIAL_6:
        ticketTemplateImageKey = selectedGameData.specialTicketTemplateImageKey6;
        template = selectedGameData.specialTemplate6;
        break;
      default:
    }

    const isGa = StorageServiceInstance.selectedSectionIsSpecial;
    const ticketSection = isGa
      ? (StorageServiceInstance.selectedSpecialSection || "General Admission")
      : StorageServiceInstance.selectedSection;

    return prepareAndSaveTicketImage(
      imageData,
      ticketTemplateImageKey,
      template,
      ticketSection,
      isGa ? "" : StorageServiceInstance.selectedRow,
      isGa ? "" : StorageServiceInstance.selectedSeat,
      isGa ? "" : StorageServiceInstance.selectedSuite,
      onlyPrepare,
      headerData,
      s3Folder,
      filename,
      StorageServiceInstance.blurImage,
      showWatermark,
      watermarkLogoOpacity,
      isGa,
    );
  }

  public saveGeneratedImage = async (base64Image: string) => {
    const file = await urltoFile(base64Image, "momento.png", "image/png");
    const fileName = `${Date.now()}.png`;

    const result = await uploadToS3ServerLargeImage(file, fileName, "image/png");

    return result;
  }

  public downloadWholeMoment = async () => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArray(true);

    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Promises = downloadDataArray.map((d) => convertToBase64(d, sponsorImage, headerData));

    const base64Images = await Promise.all(base64Promises);

    if (browserName === "chrome") {
      base64Images.forEach((image, index) => {
        this.tryUploadDownloadToS3(image, "image");
        this.createUrlAndDownloadImage(image, `${downloadDataArray[index].name}.png`);
      });
    } else {
      base64Images.forEach((image) => this.tryUploadDownloadToS3(image, "image"));
      this.safariLoader(downloadDataArray, base64Images, 0);
    }
  };

  /**
   * Downloads a single momento image (for preview modal)
   */
  public downloadSingleMomentoFromPreview = async (momentId: string, momentoIndex: number) => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArrayForMoment(momentId);
    const item = downloadDataArray[momentoIndex];
    if (!item) return;

    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Image = await convertToBase64(item, sponsorImage, headerData);

    this.tryUploadDownloadToS3(base64Image, "image");
    this.createUrlAndDownloadImage(base64Image, `${item.name}.png`);
    StorageServiceInstance.requestTeamPopupForOccurrence("momento_image_download");
  };

  /**
   * Downloads all photos from a single moment
   */
  public downloadMoment = async (momentId: string) => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArrayForMoment(momentId);
    if (!downloadDataArray.length) return;

    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Promises = downloadDataArray.map((d) => convertToBase64(d, sponsorImage, headerData));

    const base64Images = await Promise.all(base64Promises);

    const isWebView = this.isWebView();
    const imageWindow = isWebView ? null : window.open(`${Routes.IMAGE}`, "_blank");

    if (imageWindow) {
      base64Images.forEach((image) => this.tryUploadDownloadToS3(image, "image"));
      const allUrls = base64Images.map((image, index) => {
        const url = this.createUrlAndDownloadImage(image, `${downloadDataArray[index].name}.png`, null, true);
        return `src=${url}`;
      });
      imageWindow.location.href = `${Routes.IMAGE}?name=Momentos&${allUrls.join("&")}`;
      return;
    }

    if (browserName === "chrome") {
      base64Images.forEach((image, index) => {
        this.tryUploadDownloadToS3(image, "image");
        this.createUrlAndDownloadImage(image, `${downloadDataArray[index].name}.png`);
      });
    } else {
      base64Images.forEach((image) => this.tryUploadDownloadToS3(image, "image"));
      this.safariLoader(downloadDataArray, base64Images, 0);
    }
  };

  /**
   * Downloads all photos from all moments in the game (same mechanism as downloadWholeMoment)
   */
  public downloadAllGameMomentos = async () => {
    const downloadDataArray = MomentServiceInstance.getDownloadDataArrayForGame();
    if (!downloadDataArray.length) return;

    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Promises = downloadDataArray.map((d) => convertToBase64(d, sponsorImage, headerData));

    const base64Images = await Promise.all(base64Promises);

    const isWebView = this.isWebView();
    const imageWindow = isWebView ? null : window.open(`${Routes.IMAGE}`, "_blank");

    if (imageWindow) {
      base64Images.forEach((image) => this.tryUploadDownloadToS3(image, "image"));
      const allUrls = base64Images.map((image, index) => {
        const url = this.createUrlAndDownloadImage(image, `${downloadDataArray[index].name}.png`, null, true);
        return `src=${url}`;
      });
      imageWindow.location.href = `${Routes.IMAGE}?name=Momentos&${allUrls.join("&")}`;
      StorageServiceInstance.requestTeamPopupForOccurrence("all_momentos_download");
      return;
    }

    if (browserName === "chrome") {
      base64Images.forEach((image, index) => {
        this.tryUploadDownloadToS3(image, "image");
        this.createUrlAndDownloadImage(image, `${downloadDataArray[index].name}.png`);
      });
      StorageServiceInstance.requestTeamPopupForOccurrence("all_momentos_download");
    } else {
      base64Images.forEach((image) => this.tryUploadDownloadToS3(image, "image"));
      this.safariLoader(
        downloadDataArray,
        base64Images,
        0,
        undefined,
        () => StorageServiceInstance.requestTeamPopupForOccurrence("all_momentos_download"),
      );
    }
  };

  public downloadSelectedMomentos = async (
    selectedIndexes: SelectedMomentosData,
  ) => {
    const isWebView = this.isWebView();
    const imageWindow = isWebView ? null : window.open(`${Routes.IMAGE}`, "_blank");

    const downloadDataArray = MomentServiceInstance.getDownloadSelectedMomentosArray(selectedIndexes);

    const sponsorImage = await this.loadSponsorImage();
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    const base64Promises = downloadDataArray.map((d) => convertToBase64(d, sponsorImage, headerData));

    const base64Images = await Promise.all(base64Promises);

    if (imageWindow) {
      base64Images.forEach((image) => this.tryUploadDownloadToS3(image, "image"));
      const allUrls = base64Images.map((image, index) => {
        const url = this.createUrlAndDownloadImage(image, `${downloadDataArray[index].name}.png`, null, true);
        const srcUrl = `src=${url}`;

        return srcUrl;
      });

      const allUrlsSplitted = allUrls.join("&");
      imageWindow.location.href = `${Routes.IMAGE}?name=Momentos&${allUrlsSplitted}`;

      StorageServiceInstance.requestTeamPopupForOccurrence("all_momentos_download");
      return;
    }

    if (browserName === "chrome") {
      base64Images.forEach((image, index) => {
        this.tryUploadDownloadToS3(image, "image");
        this.createUrlAndDownloadImage(image, `${downloadDataArray[index].name}.png`);
      });
      StorageServiceInstance.requestTeamPopupForOccurrence("all_momentos_download");
    } else {
      base64Images.forEach((image) => this.tryUploadDownloadToS3(image, "image"));
      this.safariLoader(
        downloadDataArray,
        base64Images,
        0,
        undefined,
        () => StorageServiceInstance.requestTeamPopupForOccurrence("all_momentos_download"),
      );
    }
  };

  private safariLoader = (
    downloadData: DownloadData[],
    imageData: string[],
    index = 0,
    imageWindows?: Window[],
    onComplete?: () => void,
  ): void => {
    this.createUrlAndDownloadImage(
      imageData[index],
      `${downloadData[index].name}.png`,
      imageWindows ? imageWindows[index] : null,
    );

    if (index < imageData.length - 1) {
      setTimeout(() => {
        this.safariLoader(downloadData, imageData, index + 1, imageWindows, onComplete);
      }, this.safariDownloadInterval);
    } else if (onComplete) {
      onComplete();
    }
  };

  private prepareImagesSequenced = async (
    downloadData: DownloadData[],
    sponsorImage,
  ) => {
    const result: string[] = [];
    const showHeader = StorageServiceInstance.selectedShowMomentCaption;
    const headerData = showHeader ? await this.prepareHeaderDataWithLogos(true) : undefined;
    for (let ind = 0, len = downloadData.length; ind < len; ind += 1) {
      // eslint-disable-next-line no-await-in-loop
      const image = await convertToBase64(
        downloadData[ind],
        sponsorImage,
        headerData,
      );
      result.push(image);
    }

    return result;
  };

  public downloadGifImageGifShot = async (downloadDataArray, base64Images) => {
    return new Promise((resolve, reject) => {
      gifshot.createGIF(
        {
          gifWidth: downloadDataArray[0].width,
          gifHeight: downloadDataArray[0].height,
          images: base64Images,
          interval: GIF_FRAME_DELAY_MS / 1000,
          numWorkers: 1,
        },
        (response: GifshotResponse) => {
          if (response.error) {
            message.error(
              "Something went wrong while creating a GIF for download",
            );
            reject(null);
          }

          resolve(response.image);
        },
      );
    });
  };

  public prepareGifForPlay = async (download = true) => {
    console.log("++++prepareGifForPlay()");
    try {
      MomentServiceInstance.setGIFPlaying(true);
      MomentServiceInstance.setGIFLoading(true);

      const gifImage = MomentServiceInstance.cacheService.cacheForIndex(0).gifImage.src;

      console.log(`++++ gifImage: ${gifImage}`);

      if (gifImage) {
        MomentServiceInstance.setGIFLoading(false);
        MomentServiceInstance.setGIFImageToPlay(gifImage as string);
      } else {
        MomentServiceInstance.setGIFPlaying(false);
      }

      return gifImage;
    } catch (err) {
      message.error("Something went wrong while creating a GIF for download");
      MomentServiceInstance.setGIFPlaying(false);
      return null;
    }
  };

  public prepareVideoForDownload = async (
    startDownload: boolean,
    options?: { outputFormat?: CroppedVideoFormat; blobResult?: Blob },
  ): Promise<string> => {
    if (this.waitingForVideo === true) {
      message.error("Video generation is already underway");
      return null;
    }
    this.waitingForVideo = true;

    message.info(
      startDownload
        ? "Video download will start in a few seconds"
        : "Video will be ready in a few seconds",
    );

    try {
      const downloadDataArray = MomentServiceInstance.getDownloadDataArray(
        true,
        true,
      );

      downloadDataArray.forEach((moment) => {
        moment.x = Math.round(moment.x);
        moment.y = Math.round(moment.y);
        moment.width = Math.round(moment.width);
        moment.height = Math.round(moment.height);
      });

      const getCroppedVideoResponse = await MomentServiceInstance.getCroppedVideo({
        sponsorOverlayUrl:
            MomentServiceInstance.getSponsorOverlayForDownload(),
        momentArray: downloadDataArray,
        framesPerSecond: 4,
        outputFormat: options ? options.outputFormat : undefined,

        retrieveFramesInParallel: false,
        retrieveFramesViaWeb: false,
      });

      let fileDownloadUrl = null;
      if (getCroppedVideoResponse) {
        let videoBlob: Blob = null;

        if (getCroppedVideoResponse instanceof Blob) {
          videoBlob = getCroppedVideoResponse as Blob;
        } else {
          const blobMimeType = options && options.outputFormat === CroppedVideoFormat.GIF
            ? "image/gif"
            : "video/mp4";

          videoBlob = this.b64toBlob(
            getCroppedVideoResponse,
            blobMimeType,
            512,
          );
        }
        if (options !== undefined) {
          options.blobResult = videoBlob;
        }
        fileDownloadUrl = URL.createObjectURL(videoBlob);
        if (startDownload) {
          this.downloadBase64Image(
            fileDownloadUrl as string,
            `momento.${
              options && options.outputFormat === CroppedVideoFormat.GIF
                ? "gif"
                : "mp4"
            }`,
          );
        }
      }

      return fileDownloadUrl;
    } catch (err) {
      message.error(
        `Something went wrong while creating a video for download: ${err}`,
      );

      return null;
    } finally {
      this.waitingForVideo = false;
    }
  };

  private b64toBlob = (b64Data, contentType, sliceSize): Blob => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };

  public createUrlAndDownloadImage = (base64Image: string, name: string, wnd?: Window, onlyPrepare = false, imageScale?: number): string | null => {
    if (base64Image) {
      const parts = base64Image.split(";base64,");

      const imageType = parts[0].split(":")[1];

      const imageContent = parts[1];

      const imageBlob = this.b64toBlob(
        imageContent,
        imageType,
        512,
      );

      const fileDownloadUrl = URL.createObjectURL(imageBlob);

      this.downloadBase64Image(
           fileDownloadUrl as string,
           name,
           wnd,
           onlyPrepare,
           imageScale,
      );

      return fileDownloadUrl;
    }

    return null;
  }

  public downloadBase64Image = (dataURL: string, name: string, wnd?: Window, onlyPrepare = false, imageScale?: number): void => {
    if (!onlyPrepare) {
      const lower = name.toLowerCase();
      let assetType = "image";
      if (lower.endsWith(".gif")) assetType = "gif";
      else if (lower.endsWith(".mp4") || lower.endsWith(".webm")) assetType = "video";
      else if (lower === "ticket.png") assetType = "ticket";
      trackFanFileDownload({ file_name: name, asset_type: assetType });
    }
    if (onlyPrepare) {
      return;
    }

    if (!wnd) {
      const linkEl = document.createElement("a");
      linkEl.href = dataURL;
      linkEl.download = name;

      document.body.appendChild(linkEl);
      linkEl.click();
      document.body.removeChild(linkEl);

      return;
    }

    if (dataURL) {
      const url = imageScale
        ? `${Routes.IMAGE}?src=${dataURL}&name=${name}&scale=${imageScale}`
        : `${Routes.IMAGE}?src=${dataURL}&name=${name}`;
      wnd.location.href = url;
    } else {
      wnd.close();
    }
  };

  private convertToImageCanvas = (
    canvasWidth,
    canvasHeight,
    downloadData: DownloadData,
    sponsorImage?: HTMLImageElement,
  ): Promise<any> => {
    const { x, y, width, height, link } = downloadData;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = link;

    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const context = canvas.getContext("2d");

    if (!context) {
      console.log("No memory for canvas");
      return null;
    }

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const ratioX = width / canvasWidth;
        const ratioY = height / canvasHeight;
        const ratio = ratioX > ratioY ? ratioX : ratioY;
        const drawWidth = width / ratio;
        const drawHeight = height / ratio;
        const offsetX = (canvasWidth - drawWidth) / 2;
        const offsetY = (canvasHeight - drawHeight) / 2;

        context.drawImage(
          img,
          x,
          y,
          width,
          height,
          offsetX,
          offsetY,
          drawWidth,
          drawHeight,
        );

        if (sponsorImage) {
          renderSponsorOverlay(
            context,
            sponsorImage,
            0,
            0,
            canvasWidth,
            canvasHeight,
          );
        }

        resolve(canvas);
      };
      img.onerror = reject;
    });
  };

  private loadSponsorImage = (): Promise<HTMLImageElement> => {
    const sponsorLink = MomentServiceInstance.getSponsorOverlayForDownload();

    return new Promise((resolve, reject) => {
      if (!sponsorLink) {
        resolve(undefined);
      }

      const sponsorImg = new Image();
      sponsorImg.crossOrigin = "anonymous";
      sponsorImg.src = sponsorLink;

      sponsorImg.onload = () => {
        resolve(sponsorImg);
      };
      sponsorImg.onerror = reject;
    });
  };

  private shareViaNavigator = (
    shareFiles: any,
    shareText: string,
    fileTypeForUserNotifications: string,
    buttonColor?: string,
  ) => {
    if (!navigator.share) {
      message.error(`Your system doesn't support sharing ${fileTypeForUserNotifications} files.`);
      return;
    }

    trackFanSocialShare("native", {
      share_method: "navigator_share_files",
      ui_surface: "moment_share",
      file_count: Array.isArray(shareFiles) ? shareFiles.length : 0,
    });

    for (const file of shareFiles) {
      this.logShareEvent();
    }

    const shareData = {
      title: "Check out my authentic gameday reaction! @seeyourmomento #MyMomento",
      files: shareFiles,
    };

    navigator.share(shareData).then(() => {
      console.log("FileLoaderService.shareViaNavigator() - Share was successful.");
    }).catch((error) => {
      console.log("FileLoaderService.shareViaNavigator() - Sharing failed", error);
      message.error(
        error?.message?.includes("user gesture") || error?.message?.includes("not allowed")
          ? "Please tap the share button again to share."
          : `Error sharing ${fileTypeForUserNotifications} content`,
      );
    });
  };

  public directSharing = (shareFiles: any, fileTypeForUserNotifications?: string, buttonColor?: string) => {
    this.shareViaNavigator(shareFiles, "", fileTypeForUserNotifications || "Image", buttonColor);
  };

  public downloadGifMoment = async (wnd?: Window) => {
    const targetMomentIndex = MomentServiceInstance.momentList.findIndex(
      (m) => m._id === MomentServiceInstance.selectedMoment._id,
    );

    if (
      targetMomentIndex >= 0
      && targetMomentIndex < MomentServiceInstance.momentsData.length
    ) {
      if (MomentServiceInstance.cacheService.loading) {
        message.info("Please wait for a few seconds to prepare all images and repeat");
        if (wnd) {
          wnd.close();
        }
        return;
      }

      message.info("GIF is being created. Please wait for a few seconds");
      const momentData = MomentServiceInstance.momentsData[targetMomentIndex];

      const gifImage = await MomentServiceInstance.cacheService.renderSingleGifForSharing(
        MomentServiceInstance.selectedMoment._id,
      );

      if (gifImage) {
        this.tryUploadDownloadToS3(gifImage, "gif");
        this.createUrlAndDownloadImage(gifImage, "moment.gif", wnd);
        return;
      }

      message.error("Error during GIF generation");
      if (wnd) {
        wnd.close();
      }
      return;
    }

    message.error("No GIF Image");
    if (wnd) {
      wnd.close();
    }
  };
}

export const FileLoaderServiceInstance = new FileLoaderService();
